import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminRole } from '../auth/permissions';
import { useDialogs } from '../../components/PromptDialog';
import { createUploadKey, downloadUploadKey, fetchUploadKeys, revokeUploadKey, type CreatedUploadKey } from './uploadKeys';

const field = 'mt-1 block min-h-[44px] w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-text-primary';
const button = 'min-h-[44px] rounded-md bg-accent px-4 py-2 font-semibold text-white disabled:opacity-50';
const date = (value: string | null) => value ? new Date(value).toLocaleDateString('de-DE') : 'Noch nicht';

export function UploadKeysPage(): JSX.Element {
  const role = useAdminRole();
  // Keine Abfrage und kein Geheimnis im Komponentenstate ohne bestätigte ADMIN-Rolle.
  return role === 'ADMIN' ? <UploadKeysManager /> : <p>Upload-Schlüssel kann nur ein Admin verwalten.</p>;
}

function UploadKeysManager(): JSX.Element {
  const keys = useQuery({ queryKey: ['social', 'upload-keys'], queryFn: fetchUploadKeys });
  const dialogs = useDialogs();
  const [name, setName] = useState('Codex Upload');
  const [permanent, setPermanent] = useState(false);
  const [created, setCreated] = useState<CreatedUploadKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!name.trim() || busy || created) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      // Bewusst keine useMutation-Daten: das Geheimnis gehört nicht in den Query-Cache.
      setCreated(await createUploadKey(name.trim(), permanent ? null : 90));
      await keys.refetch();
    } catch (e) { setError(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  async function revoke(id: string, keyName: string) {
    if (!await dialogs.confirm({ title: 'Upload-Schlüssel widerrufen?', message: `„${keyName}“ kann anschließend keine Uploads mehr ausführen.`, confirmLabel: 'Widerrufen', destructive: true })) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      await revokeUploadKey(id);
      if (created?.id === id) setCreated(null);
      await keys.refetch();
      setMessage('Upload-Schlüssel widerrufen.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Widerruf fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  function download() {
    if (!created) return;
    setError(null);
    try {
      downloadUploadKey(created);
      setCreated(null);
      setMessage('Konfiguration heruntergeladen. Der Schlüssel wird hier nicht erneut angezeigt.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Download fehlgeschlagen.'); }
  }

  return <div className="mx-auto max-w-3xl space-y-6 text-sm">
    <header>
      <h1 className="text-xl font-bold">Upload-Schlüssel</h1>
      <p className="mt-2 text-text-secondary">Für Video-Uploads per API ohne erneute Browser-Anmeldung. Die Schlüssel gelten nur für Social-Uploads und bleiben jederzeit widerrufbar.</p>
    </header>
    <form onSubmit={(e) => { void create(e); }} className="space-y-4 rounded-xl border border-border-subtle bg-bg-surface p-5">
      <h2 className="font-semibold">Neuen Schlüssel anlegen</h2>
      <label className="block">Name<input className={field} value={name} maxLength={80} required disabled={busy || !!created} onChange={(e) => setName(e.target.value)} /></label>
      <label className="flex min-h-[44px] items-center gap-3"><input type="checkbox" checked={permanent} disabled={busy || !!created} onChange={(e) => setPermanent(e.target.checked)} />Ohne Ablaufdatum</label>
      <p className="text-text-secondary">{permanent ? 'Gültig bis zum Widerruf. Auch eine Admin-Sperre oder der Verlust der Admin-Rolle beendet den Zugriff.' : 'Gültig für 90 Tage.'}</p>
      <button className={button} disabled={busy || !!created || !name.trim()}>{busy ? 'Bitte warten …' : 'Schlüssel anlegen'}</button>
    </form>
    {created && <section aria-label="Konfiguration herunterladen" className="space-y-3 rounded-xl border border-border-subtle bg-bg-surface p-5">
      <h2 className="font-semibold">Schlüssel angelegt</h2>
      <p>Die Konfiguration kannst du jetzt einmalig herunterladen. Sie enthält den geheimen Upload-Schlüssel.</p>
      <button type="button" className={button} onClick={download}>Konfiguration herunterladen</button>
      <p className="break-words text-text-secondary">Lokal unter <code>~/.config/currivium/social-upload.env</code> speichern und den Dateizugriff auf deinen Benutzer beschränken. Die Upload-Werkzeuge lesen die Datei automatisch.</p>
    </section>}
    {error && <p role="alert" className="text-danger">{error}</p>}
    {message && <p role="status" className="text-success">{message}</p>}
    <section aria-label="Vorhandene Upload-Schlüssel" className="space-y-3">
      <h2 className="font-semibold">Vorhandene Schlüssel</h2>
      {keys.isLoading && <p>Schlüssel werden geladen …</p>}
      {keys.isError && <div role="alert"><p>Schlüssel konnten nicht geladen werden.</p><button type="button" className={button} onClick={() => { void keys.refetch(); }}>Erneut laden</button></div>}
      {keys.data?.keys.length === 0 && <p className="text-text-secondary">Noch keine Upload-Schlüssel.</p>}
      {keys.data?.keys.map((key) => <article key={key.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-subtle bg-bg-surface p-4">
        <div className="min-w-0 break-words">
          <h3 className="font-semibold">{key.name}</h3>
          <p className="text-text-secondary">{key.prefix}… · {key.revokedAt ? `Widerrufen am ${date(key.revokedAt)}` : key.expiresAt === null ? 'Unbefristet' : `${new Date(key.expiresAt) <= new Date() ? 'Abgelaufen am' : 'Gültig bis'} ${date(key.expiresAt)}`}</p>
          <p className="text-text-secondary">Zuletzt verwendet: {date(key.lastUsedAt)}</p>
        </div>
        {!key.revokedAt && <button type="button" aria-label={`Widerrufen: ${key.name}`} className="min-h-[44px] rounded-md px-4 py-2 text-danger disabled:opacity-50" disabled={busy} onClick={() => { void revoke(key.id, key.name); }}>Widerrufen</button>}
      </article>)}
    </section>
    {dialogs.dialog}
  </div>;
}
